/**
 * Common utilities for transforming field structures across the application
 */

import { normalizeFieldType } from './typeMappers';

export interface Field {
  name: string;
  field_name?: string;
  display_name: string;
  field_type: string;
  is_required: boolean;
  is_list?: boolean;
  description?: string;
  help_text?: string;
  array_item_type?: string;
  reference_object_id?: string;
  reference_object_name?: string;
  allowed_values?: string[];
  reference_object?: any;
}

export interface NestedField extends Field {
  fields?: Field[];
}

/**
 * Transform flat dot-notation fields into properly nested structures
 * e.g., "macros.calories" becomes a nested structure with macros containing calories
 */
export function transformDotNotationToNested(fields: Field[]): Field[] {
  const result: Field[] = [];
  const nestedGroups: Map<string, Field[]> = new Map();
  
  fields.forEach(field => {
    const fieldName = field.field_name || field.name;
    if (!fieldName) return;
    
    if (fieldName.includes('.')) {
      // This is a nested field
      const parts = fieldName.split('.');
      const parentName = parts[0];
      const childName = parts.slice(1).join('.');
      
      if (!nestedGroups.has(parentName)) {
        nestedGroups.set(parentName, []);
      }
      
      nestedGroups.get(parentName)!.push({
        ...field,
        name: childName,
        field_name: childName,
        display_name: field.display_name || childName.split('_').map(w => 
          w.charAt(0).toUpperCase() + w.slice(1)
        ).join(' ')
      });
    } else {
      // This is a regular field
      result.push(field);
    }
  });
  
  // Convert nested groups into json fields with nested structure
  nestedGroups.forEach((nestedFields, parentName) => {
    const parentField: NestedField = {
      name: parentName,
      display_name: parentName.split('_').map(w => 
        w.charAt(0).toUpperCase() + w.slice(1)
      ).join(' '),
      field_type: 'json',
      is_required: nestedFields.every(f => f.is_required),
      description: `${parentName} information`,
      fields: transformDotNotationToNested(nestedFields) // Recursive for deeper nesting
    };
    result.push(parentField);
  });
  
  return result;
}

/**
 * Transform AI response field format to standard format
 * Handles both field_name and name properties
 */
export function transformFieldFormat(field: any): Field {
  return {
    name: field.field_name || field.name,
    display_name: field.display_name || field.field_name || field.name,
    field_type: normalizeFieldType(field.type || field.field_type, 'ai'),
    is_required: field.required !== undefined ? field.required : field.is_required,
    is_list: field.is_list || false,
    description: field.description || field.help_text || '',
    array_item_type: field.array_item_type ? normalizeFieldType(field.array_item_type, 'ai') : undefined,
    reference_object_id: field.reference_object_id,
    reference_object_name: field.reference_object_name,
    allowed_values: field.allowed_values,
  };
}

/**
 * Transform an entire object including handling nested fields
 */
export function transformObjectWithNestedFields(obj: any): any {
  if (!obj || !obj.fields) return obj;
  
  const transformedFields = obj.fields.map(transformFieldFormat);
  const nestedFields = transformDotNotationToNested(transformedFields);
  
  return {
    ...obj,
    fields: nestedFields
  };
}

/**
 * Extract common field mapping logic used across components
 */
export function mapFieldForPydantic(field: any, customObjects: any[]): any {
  const fieldName = field.field_name || field.name;
  
  // Try to get reference object name if we have the ID but not the name
  let refObjName = field.reference_object_name;
  if (!refObjName && field.reference_object_id) {
    const refObj = customObjects.find(o => o.id === field.reference_object_id);
    if (refObj) {
      refObjName = refObj.name;
    }
  }
  
  const mappedField: any = {
    name: fieldName,
    display_name: field.display_name || fieldName,
    field_type: field.field_type,
    is_required: !!field.is_required,
    is_array: !!field.is_array,
    description: field.description || field.help_text || '',
    allowed_values: field.allowed_values,
    array_item_type: field.array_item_type,
    reference_object_id: field.reference_object_id,
    reference_object_name: refObjName,
  };
  
  // Handle nested fields - check both 'fields' and 'nested_fields' properties
  const nestedFields = field.fields || field.nested_fields;
  if (nestedFields && Array.isArray(nestedFields)) {
    mappedField.fields = nestedFields.map((f: any) => mapFieldForPydantic(f, customObjects));
  }
  
  // Handle reference object - load from customObjects if not already present
  // This applies to both direct reference fields and array fields with reference items
  const needsReferenceObject = (
    (field.field_type === 'reference' && !field.reference_object) ||
    (field.field_type === 'array' && field.array_item_type === 'reference' && !field.reference_object)
  );
  
  if (needsReferenceObject && (field.reference_object_id || refObjName)) {
    // Prioritize ID-based lookup since ID is more reliable than name
    let refObj = null;
    
    if (field.reference_object_id) {
      refObj = customObjects.find(o => o.id === field.reference_object_id);
    }
    
    // Fallback to name-based lookup if ID lookup failed
    if (!refObj && refObjName) {
      refObj = customObjects.find(o => 
        o.name === refObjName || 
        o.name === field.reference_object_name ||
        o.display_name === refObjName
      );
    }
    
    if (refObj && refObj.fields && refObj.fields.length > 0) {
      mappedField.reference_object = {
        name: refObj.name,
        display_name: refObj.display_name,
        description: refObj.description,
        fields: refObj.fields.map((rf: any) => mapFieldForPydantic(rf, customObjects))
      };
    }
  } else if (field.reference_object && customObjects) {
    // If reference_object is already present, map its fields
    const refObj = customObjects.find(o => o.id === field.reference_object_id || o.name === field.reference_object_name);
    if (refObj && refObj.fields && refObj.fields.length > 0) {
      mappedField.reference_object = {
        name: refObj.name,
        display_name: refObj.display_name,
        description: refObj.description,
        fields: refObj.fields.map((rf: any) => mapFieldForPydantic(rf, customObjects))
      };
    }
  }
  
  return mappedField;
}
